import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import moment from "moment";
import { Tooltip } from "@mui/material";

// Component imports
import CustomButton from "../../../atoms/CustomButton";
import CustomDataGrid from "../../../organisms/CustomDatagrid";
import CustomMenu from "../../../atoms/CustomMenu";
import NoContentCard from "../../../atoms/NoContentCard";
import Search from "../../../molecules/search";
import Pagination from "../../../molecules/Pagination";
import DeleteModal from "../../../organisms/modals/DeleteModal";
import Modal from "../../../organisms/modals/Modal";

// Icon imports
import VerticalMenu from "../../../icons/VerticalMenu";
import Edit from "../../../icons/Edit";
import Delete from "../../../icons/Delete";
import {
  errorStyles,
  confimationStyles,
} from "../../../../assets/styles/toast";

// Service imports
import { getData, deleteData } from "../../../../services";
import { formatTime } from "../../../../helpers/timeformathoursminsec";

// DSR Form component
import DSRForm from "../dailyStatusReport/Form";
import DateRangePickerShortCuts from "../../../organisms/dateRangePicker";
import { addHours,startOfDay,subDays } from "rsuite/esm/internals/utils/date";
import StatusBackground from "../../../atoms/StatusBackground";

const DailyStatusReport = () => {
  const [cookies] = useCookies([]);
  const [dsrData, setDsrData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [todayDsrExists, setTodayDsrExists] = useState(false);
  const [modalState, setModalState] = useState({
    isModalOpen: false,
    modalData: null,
    isDeleteModalOpen: false,
    modalId: "",
    name: "",
    title: ""
  });
  const defaultToday = new Date();

   const [date, setDate] = useState({
      start_date: addHours(startOfDay(subDays(defaultToday, 14)), 12), // 14 days ago
      end_date: addHours(startOfDay(defaultToday), 12), // Today
    });

  // Modified: Removed isAdmin check - everyone can create, edit, delete
  const canCreate = true;
  const canEdit = true;
  const canDelete = true;

  // Fetch DSR data
  const getDSRData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const res = await getData({
        endpoint: "StatusReport/GetDSRByEmployeeIdAsync",
        params:{
          start_date: date?.start_date,
          end_date : date?.end_date,
        },
        token: cookies.e_t,
      });
      
      if (res) {
 
        setDsrData(res?.data);

        const today = moment().format("YYYY-MM-DD");
        const todayEntry = res?.data?.find(entry => 
          moment(entry.dsr_date).format("YYYY-MM-DD") === today
        );
        
        setTodayDsrExists(!!todayEntry);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [cookies.e_t, currentPage, itemsPerPage, searchedValue,date]);

  useEffect(() => {
    getDSRData();
  }, [getDSRData]);

  // Menu handling - modified to show all options for everyone
  const menu = [
    {
      icon: <Edit height="20" width="20" />,
      label: "Edit",
      onClick: () => {
        setAnchorEl(null);
        handleEdit(selectedRow);
      },
    },
    // {
    //   icon: <Delete />,
    //   label: "Delete",
    //   onClick: () => {
    //     setAnchorEl(null);
    //     handleDeleteModal(selectedRow);
    //   },
    // },
  ];

  const handleDeleteModal = (data) => {
    setModalState((prevState) => ({
      ...prevState,
      isDeleteModalOpen: true,
      modalId: data.id,
      name: data.name,
    }));
  };

  const handleDeletion = async () => {
    try {
      const res = await deleteData({
        endpoint: `StatusReport/DeleteDSRAsync?id=${modalState.modalId}`,
        token: cookies.e_t,
      });
      
      if (res) {
        getDSRData();
        toast.success("DSR Entry Deleted Successfully", {
          style: { 
            background: '#34A853', 
            color: '#fff' 
          },
          duration: 1000,
        });
      }
    } catch (error) {
      toast.error("An Error Occurred", {
        style: { 
          background: '#D32F2F', 
          color: '#fff' 
        },
        duration: 1000,
      });
      console.error(error);
    } finally {
      setModalState((prevState) => ({
        ...prevState,
        isDeleteModalOpen: false,
      }));
    }
  };


  function parseTimeToMinutes(timeStr) {
    if (!timeStr || typeof timeStr !== "string") return 0;

    const hoursMatch = timeStr.match(/(\d+)h/);
    const minutesMatch = timeStr.match(/(\d+)m/);

    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

    return hours * 60 + minutes;
}

  const handleStatusMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((perPage) => {
    setCurrentPage(1);
    setItemsPerPage(perPage);
  }, []);

  const countTasksWithTime = (tasks) => {
    return tasks.filter(task => {
      if (!task.totalElapsedTime) return false;
      const [hoursStr, minsStr] = task.totalElapsedTime.split(' ');
      const hours = parseInt(hoursStr.replace('h', ''));
      const minutes = parseInt(minsStr.replace('m', ''));
      return hours > 0 || minutes > 0;
    }).length;
  }
  

  const handleEdit = (tableData) => {
    setModalState((prevState) => ({
      ...prevState,
      modalId: tableData.id,
      modalData: tableData,
      isModalOpen: true,
      title: "Edit DSR Entry",
    }));
  };

  const columns = [
    {
      field: "dsr_date",
      headerName: "Dsr Date",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        return (
          <div>
             {moment(params?.value).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      field: "totalLoggedTime",
      headerName: "Hours Logged",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        // Parse time in format "D.HH:MM:SS" or "HH:MM:SS"
        const parseTimeToMinutes = (timeStr) => {
          if (!timeStr || typeof timeStr !== "string") return 0;
          
          let days = 0;
          let timeComponent = timeStr;
          
          // Check if the format includes days (D.HH:MM:SS)
          if (timeStr.includes('.')) {
            const parts = timeStr.split('.');
            days = parseInt(parts[0], 10) || 0;
            timeComponent = parts[1];
          }
          
          // Parse the HH:MM:SS portion
          if (timeComponent.includes(':')) {
            const [hours, minutes, seconds] = timeComponent.split(':').map(num => parseInt(num, 10));
            // Calculate total minutes including days
            return (days * 24 * 60) + ((hours || 0) * 60) + (minutes || 0);
          }
          
          return 0;
        };
        
        // Make sure we're working with a string
        const timeStr = String(params.value || "");
        
        // Parse the time value to minutes
        const timeInMinutes = parseTimeToMinutes(timeStr);
        
        // Check if over 24 hours (1440 minutes)
        const isOverTime = timeInMinutes > 1440;
        
        return (
          <span
            style={{
              fontWeight: isOverTime ? "600" : "none",
              color: isOverTime ?" #be0f00" : "none",
            }}
          >
            {formatTime(timeStr)}
          </span>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        return (
          <div>
             {moment(params?.value).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      field: "modified_at",
      headerName: "Updated At",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        return (
          <div>
             {moment(params?.value).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      field: "totalTasks",
      headerName: "Total Task",
      flex: 1.5,
      sortable: true,
      renderCell : (params) => {
        return(
          <>{countTasksWithTime(params?.row?.taskSummaries)}</>
        )
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return <StatusBackground status={params.value} />;
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.2,
      renderCell: (params) => {
        return (
          <div className="table-icons-container">
            <div onClick={(event) => handleStatusMenu(event, params.row)}>
              <VerticalMenu />
            </div>

            <CustomMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              items={menu}
            />
          </div>
        );
      },
    },
  ];

  const openModal = (title, data = null) => {
    const modalId = data ? data.id : uuidv4();
    setModalState((prevState) => ({
      ...prevState,
      modalId,
      isModalOpen: true,
      title,
      modalData: data,
    }));
  };

  const closeModal = (type) => {
    if (type === 'submit') {
      setIsFormDirty(false);
    }
    setModalState((prevState) => ({ 
      ...prevState, 
      isModalOpen: false,
      modalData: null
    }));
  };

  const CustomNoRowsOverlay = React.memo(() => {
    return (
      <>
        <NoContentCard
          title="No DSR Found"
          type="Task"
          // subtitle={
          //   "Looks like you’re all caught up—check back later for new assignments."
          // }
        />
      </>
    );
  });

  return (
    <div id="Daily-Status-Report" className="main-container">
      <div className="d-flex justify-content-between">
        {/* <div className="d-flex gap-2">
          <Search
            placeholder="Search"
            onSearchEmit={(item) => {
              if (currentPage !== 1) {
                setCurrentPage(1);
              }
              setSearchedValue(item);
            }}
          />
        </div> */}
        <div>
        <DateRangePickerShortCuts setDate={setDate} />
        </div>
        <div className="d-flex gap-2">
          <Tooltip 
            title={todayDsrExists ? "Today's DSR already exists" : ""}
            placement="top"
          >
            <span>
              <CustomButton
                text="Add DSR"
                type="btn-primary"
                handleClick={() => openModal("Create DSR Entry")}
                
              />
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="mt-3">
        <CustomDataGrid
          columns={columns}
          CustomNoRowsOverlay={CustomNoRowsOverlay}
          rows={dsrData || []}
          isLoadingStateProvided={true}
          isLoading={isLoading}
          onCellClick={(params) => {
            if (params.field !== "actions") {
              handleEdit(params.row);
            }
          }}
          canEdit={true}
        />
      </div>

      {dsrData?.total_records > 10 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={dsrData?.total_records}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {modalState.isModalOpen && (
        <Modal
          id={modalState.modalId}
          title={modalState.title}
          isActive={modalState.isModalOpen}
          onClose={closeModal}
          takeConfirmation={isFormDirty ? true : false}
          width="800px"
          maxHeight="800px"
        >
          <DSRForm
            onFormDirty={setIsFormDirty}
            initialData={
              modalState.title === "Edit DSR Entry" ? modalState.modalData : null
            }
            refetchData={getDSRData}
            onClose={() => closeModal("submit")}
          />
        </Modal>
      )}

      {modalState.isDeleteModalOpen && (
        <DeleteModal
          name={modalState.name}
          id={modalState.modalId}
          isActive={modalState.isDeleteModalOpen}
          onClose={() =>
            setModalState((prevState) => ({
              ...prevState,
              isDeleteModalOpen: false,
            }))
          }
          title="DSR Entry"
          onClick={handleDeletion}
        />
      )}
    </div>
  );
};

export default DailyStatusReport;